import * as d3 from 'd3'
import { Area } from '@app/domain/Area'
import {
    AfterViewInit,
    Component,
    ElementRef,
    Input, OnDestroy,
    OnInit,
    ViewChild, Output, EventEmitter, ChangeDetectorRef, OnChanges, SimpleChanges,
} from '@angular/core'
import {
    FloorTableViewModel,
} from '@app/features/shared/components/area-floor/floor-table.view-model'
import { ResizeObservable } from '@app/features/shared/rjxs/resize-observable'
import {
    Subject, takeUntil,
} from 'rxjs'
import { Venue } from '@app/domain/Venue'

@Component({
    selector: 'app-floor-plan',
    templateUrl: './area-floor.component.html',
    styleUrls: ['./area-floor.component.sass'],
})
export class AreaFloorComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {

    @Input() area!: Area
    @Input() tableViewModels!: FloorTableViewModel[]
    @Input() venue!: Venue
    @Input() containerWidth = 0
    @Input() containerHeight = 0
    @Input() fitToHeight = true
    @Output() bookingHover = new EventEmitter<string | null>()
    private mouseEvents = new Subject<{ event: 'down' | 'up' | 'move', x: number, y: number }>()
    mouseEvents$ = this.mouseEvents.asObservable()
    xScale!: d3.ScaleLinear<number, number>
    yScale!: d3.ScaleLinear<number, number>
    @ViewChild('canvas', { static: true }) canvas!: ElementRef
    margin = {
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    }
    width: number = 600
    height: number = 400
    floorDimensions = {
        width: 60,
        height: 40,
    }
    private onDestroy = new Subject<void>()
    private cachedWidth: number = 0
    private cachedHeight: number = 0
    private readonly DIMENSION_CHANGE_THRESHOLD = 20

    constructor(private cdr: ChangeDetectorRef) { }

    ngOnInit() {
        this.updateFloorDimensions()
        if (this.containerWidth === 0 || this.containerHeight === 0) {
            this.bindWidthToFloorDimensions()
        }
    }

    ngAfterViewInit() {
        if (this.containerWidth === 0 && this.containerHeight === 0) {
            requestAnimationFrame(() => {
                this.updateFloorDimensions()
                this.cdr.detectChanges()
            })
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['containerWidth'] || changes['containerHeight'] || changes['fitToHeight']) {
            this.updateFloorDimensions()
        }
    }

    ngOnDestroy() {
        this.onDestroy.next()
        this.onDestroy.complete()
    }

    updateFloorDimensions() {
        let availableWidth: number
        let availableHeight: number
        
        // If container dimensions are provided, use them (diary floor plan mode)
        // Otherwise measure from canvas element directly (table selection modal mode)
        if (this.containerWidth > 0 && this.containerHeight > 0) {
            availableWidth = this.containerWidth - this.margin.left - this.margin.right
            availableHeight = this.containerHeight - this.margin.top - this.margin.bottom
            
            const areaTabsHeight = 60 // Approximate height of area tabs and separator
            
            if (!this.fitToHeight) {
                // Fixed aspect ratio mode - only scale by width (used in table selection modal)
                // Don't subtract area tabs height in this mode
                if (availableWidth <= 0) {
                    return
                }
                const width = availableWidth
                const height = width * (this.floorDimensions.height / this.floorDimensions.width)
                this.width = width
                this.height = height
            } else {
                // Fit to both dimensions mode (used in diary floor plan view)
                availableHeight = Math.max(0, availableHeight - areaTabsHeight)
                
                if (availableWidth <= 0 || availableHeight <= 0) {
                    return // Don't update if no space available
                }
                
                // Calculate the aspect ratio of the floor plan
                const floorAspectRatio = this.floorDimensions.width / this.floorDimensions.height
                const containerAspectRatio = availableWidth / availableHeight
                
                let width: number
                let height: number
                
                // Scale based on whichever dimension is the limiting factor
                if (containerAspectRatio > floorAspectRatio) {
                    // Container is wider than floor plan, height is the limiting factor
                    height = availableHeight
                    width = height * floorAspectRatio
                } else {
                    // Container is taller than floor plan, width is the limiting factor
                    width = availableWidth
                    height = width / floorAspectRatio
                }
                
                this.width = width
                this.height = height
            }
        } else {
            const rect = this.canvas.nativeElement.getBoundingClientRect()
            const width = rect.width - this.margin.left - this.margin.right
            let newWidth: number
            let newHeight: number
            
            if (width <= 0) {
                newWidth = 0
                newHeight = 0
            } else {
                newWidth = width
                newHeight = width * (this.floorDimensions.height / this.floorDimensions.width)
            }
            const widthDiff = Math.abs(newWidth - this.cachedWidth)
            const heightDiff = Math.abs(newHeight - this.cachedHeight)

            if (this.cachedWidth !== 0 && widthDiff < this.DIMENSION_CHANGE_THRESHOLD && heightDiff < this.DIMENSION_CHANGE_THRESHOLD) {
                return
            }
            this.cachedWidth = newWidth
            this.cachedHeight = newHeight
            this.width = newWidth
            this.height = newHeight
        }
        
        this.xScale = d3.scaleLinear()
            .domain([0, this.floorDimensions.width])
            .range([0, this.width])
        this.yScale = d3.scaleLinear()
            .domain([0, this.floorDimensions.height])
            .range([0, this.height])
    }

    onMouseDown(event: MouseEvent) {
        event.preventDefault()
        const [x, y] = this.translateToFloorPosition(event.clientX, event.clientY)
        this.mouseEvents.next({
            event: 'down',
            x,
            y,
        })
    }

    onTouchStart(event: TouchEvent) {
        event.preventDefault()
        const touch = event.touches[0]
        const [x, y] = this.translateToFloorPosition(touch.clientX, touch.clientY)
        this.mouseEvents.next({
            event: 'down',
            x,
            y,
        })
    }

    onMouseMove(event: MouseEvent) {
        const [x, y] = this.translateToFloorPosition(event.clientX, event.clientY)
        this.mouseEvents.next({
            event: 'move',
            x,
            y,
        })
    }

    onTouchMove(event: TouchEvent) {
        event.preventDefault()
        const touch = event.touches[0]
        const [x, y] = this.translateToFloorPosition(touch.clientX, touch.clientY)
        this.mouseEvents.next({
            event: 'move',
            x,
            y,
        })
    }

    onMouseUp(event: MouseEvent) {
        event.preventDefault()
        const [x, y] = this.translateToFloorPosition(event.clientX, event.clientY)
        this.mouseEvents.next({
            event: 'up',
            x,
            y,
        })
    }

    onTouchEnd(event: TouchEvent) {
        event.preventDefault()
        const touch = event.changedTouches[0]
        const [x, y] = this.translateToFloorPosition(touch.clientX, touch.clientY)
        this.mouseEvents.next({
            event: 'up',
            x: x,
            y: y,
        })
    }

    translateToFloorPosition(x: number, y: number) {
        const element = this.canvas.nativeElement
        const rect = element.getBoundingClientRect()
        return [x - rect.left, y - rect.top]
    }

    onBookingHover(bookingId: string | null) {
        this.bookingHover.emit(bookingId)
    }

    private bindWidthToFloorDimensions() {
        const resizeObservable = new ResizeObservable(this.canvas.nativeElement)
        resizeObservable
            .pipe(takeUntil(this.onDestroy))
            .subscribe(() => {
                this.updateFloorDimensions()
            })
    }
}
